import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Lock, LogOut, Download, Users, Phone, Camera, CalendarClock, Eye, X, RefreshCw, Search, ArrowUpCircle, Trash2, Mail, Loader2, Database, Share2, ChevronRight, QrCode, Euro, Check, EyeOff, PlayCircle, Pencil, Upload, Building2, FileText } from "lucide-react";
import CompetitorRadar from "@/components/enea/CompetitorRadar";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, Legend, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "enea_admin_token";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const fmtEur = (v) => (v || v === 0 ? `€ ${Number(v).toLocaleString("it-IT")}` : "—");

const authApi = () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` } });

function ComparablesCard() {
  const [total, setTotal] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = async () => {
    try { const { data } = await authApi().get("/admin/comparables?limit=0"); setTotal(data.total); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await authApi().post("/admin/comparables/upload", fd);
      toast.success(`Comparabili caricati: ${data.inserted}${data.skipped ? ` (scartati ${data.skipped})` : ""}`);
      setTotal(data.total);
    } catch { toast.error("Caricamento CSV non riuscito"); }
    setUploading(false);
    e.target.value = "";
  };
  return (
    <div className="bg-[#0b2545] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-comparables-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5 text-teal-300" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">Comparabili reali · compravendite Gruppo Vela</div>
          <div className="text-[11px] text-slate-400 mt-0.5" data-testid="admin-comparables-count">
            {total === null ? "—" : `${total} compravendite in archivio`} — le valutazioni mostrano i 3 immobili più simili (≥80%); senza dati reali usano immobili tipo OMI
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">CSV con colonne: comune; indirizzo; tipologia; mq; prezzo; stato; data; fonte (separatore ; o ,)</div>
        </div>
      </div>
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="admin-comparables-file" />
      <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-white/10 hover:bg-white/20 text-white" data-testid="admin-comparables-upload-btn">
        {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Carica CSV
      </Button>
    </div>
  );
}

function LoginGate({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Errore di accesso");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#081c33] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#0b2545] border border-white/10 rounded-2xl p-8 space-y-5" data-testid="admin-login-form">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-full bg-teal-500/15 border border-teal-400/30 flex items-center justify-center mx-auto">
            <Lock className="w-5 h-5 text-teal-300" />
          </div>
          <h1 className="text-xl font-semibold text-white">Area Riservata</h1>
          <p className="text-xs text-slate-400 tracking-widest uppercase">Gruppo Vela · Home Exchange Copilot AI</p>
        </div>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
          className="bg-white/5 border-white/15 text-white placeholder:text-slate-500" data-testid="admin-password-input" autoFocus />
        {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <Button type="submit" disabled={loading || !password} className="w-full bg-teal-500 hover:bg-teal-400 text-[#081c33] font-semibold" data-testid="admin-login-submit">
          {loading ? "Accesso..." : "Accedi"}
        </Button>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="bg-[#0b2545] border border-white/10 rounded-xl p-4 flex items-center gap-3 text-left w-full transition-colors hover:border-teal-400/60 hover:bg-[#0e2c52] group cursor-pointer">
      <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-teal-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-2xl font-bold text-white leading-none">{value ?? "—"}</div>
        <div className="text-[11px] text-slate-400 uppercase tracking-wider mt-1">{label}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-teal-300 transition-colors shrink-0" />
    </button>
  );
}

const FeedbackStars = ({ n }) => (
  <span className="text-amber-300 text-sm tracking-widest">
    {"★".repeat(n || 0)}<span className="text-slate-600">{"★".repeat(5 - (n || 0))}</span>
  </span>
);

const GpsBadge = ({ value }) => {
  if (value === true) return <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-300 font-semibold" title="Posizione foto corrisponde all'indirizzo">GPS ✓</span>;
  if (value === false) return <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-semibold" title="Posizione foto NON corrisponde all'indirizzo">GPS ✗</span>;
  return null;
};

function LeadDetail({ lead, onClose }) {
  const [resending, setResending] = useState(false);
  const resendEmail = async () => {
    if (resending) return;
    setResending(true);
    try {
      const { data } = await authApi().post(`/admin/leads/${lead.id}/resend-email`);
      toast.success(`Email reinviata a ${data.sent_to}${data.with_pdf ? " (con PDF allegato)" : ""}`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Reinvio non riuscito");
    } finally {
      setResending(false);
    }
  };
  const val = lead.valuation || {};
  const rows = [
    ["Nome", lead.full_name], ["Email", lead.email], ["Telefono", lead.phone],
    ["Obiettivo", lead.goal_label || lead.goal], ["Tolleranza rischio", lead.risk_tolerance === "vincolate" ? "🟠 Accetta vincolate (da rieducare)" : lead.risk_tolerance === "certificati" ? "🟢 Solo certificati" : null], ["Intenzione di vendita", lead.sale_intent === "vendo_subito" ? "🔥 Vende SUBITO" : lead.sale_intent === "entro_3_mesi" ? "🔥 Entro 3 mesi" : lead.sale_intent === "entro_6_mesi" ? "Entro 6 mesi" : lead.sale_intent === "oltre_6_mesi" ? "Oltre 6 mesi" : lead.sale_intent === "solo_informativa" ? "Solo informativa" : null], ["Condizione esterna", lead.exterior_condition], ["Usufrutto", lead.usufruct && lead.usufructuary_age ? `Usufruttuario di ${lead.usufructuary_age} anni` : null], ["Modalità", lead.valuation_mode], ["Tier", lead.photo_tier],
    ["Indirizzo", lead.property_address], ["Comune", lead.comune], ["Zona", lead.zona],
    ["Tipologia", lead.property_type], ["Superficie", lead.property_sqm ? `${lead.property_sqm} mq` : null],
    ["Anno", lead.construction_year], ["Stato", lead.stato_conservativo],
    ["Giardino", lead.garden_sqm ? `${lead.garden_sqm} mq` : null], ["Terrazzo", lead.terrazzo_sqm ? `${lead.terrazzo_sqm} mq` : null],
    ["Verifica GPS foto", lead.gps_match === true ? "Corrisponde all'indirizzo ✓" : lead.gps_match === false ? "NON corrisponde — da verificare ✗" : null],
    ["Posizione rilevata dalla foto", lead.photo_gps_label],
    ["Beta tester", lead.beta ? "Sì 🧪" : null],
    ["Note", lead.notes], ["ID Pratica", lead.id], ["Data", fmtDate(lead.timestamp)],
  ].filter(([, v]) => v);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="lead-detail-modal">
      <div className="bg-[#0b2545] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">{lead.full_name}</h2>
            <p className="text-xs text-slate-400">{fmtDate(lead.timestamp)}</p>
          </div>
          <div className="flex items-center gap-2">
            {lead.email && (
              <button onClick={resendEmail} disabled={resending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500 text-[#081c33] text-xs font-semibold hover:bg-teal-400 disabled:opacity-60"
                data-testid="lead-resend-email-btn">
                {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Reinvia email + PDF
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="lead-detail-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {val.estimated_value_median && (
          <div className="bg-teal-500/10 border border-teal-400/25 rounded-xl p-4 mb-4 grid grid-cols-3 gap-2 text-center">
            <div><div className="text-[10px] text-slate-400 uppercase">Min</div><div className="text-sm font-semibold text-white">{fmtEur(val.estimated_value_min)}</div></div>
            <div><div className="text-[10px] text-teal-300 uppercase">Media MCM</div><div className="text-lg font-bold text-teal-300">{fmtEur(val.estimated_value_median)}</div></div>
            <div><div className="text-[10px] text-slate-400 uppercase">Max</div><div className="text-sm font-semibold text-white">{fmtEur(val.estimated_value_max)}</div></div>
            {val.price_sqm && <div className="col-span-3 text-[11px] text-slate-400">Prezzo base: {fmtEur(val.price_sqm)}/mq {val.zone ? `· ${val.zone}` : ""}</div>}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-white/5 py-1.5">
              <span className="text-slate-400">{k}</span><span className="text-white text-right break-all">{String(v)}</span>
            </div>
          ))}
        </div>
        {(lead.photos || []).length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Foto ({lead.photos.length})</div>
            <div className="grid grid-cols-4 gap-2">
              {lead.photos.map((p, i) => (
                <a key={i} href={`${API}/attachment/${p.file_id}?token=${sessionStorage.getItem(TOKEN_KEY)}`} target="_blank" rel="noreferrer"
                  className="relative block aspect-square rounded-lg overflow-hidden border border-white/10 bg-white/5">
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-500 px-1 text-center">{p.room || p.filename || `foto ${i + 1}`}</span>
                  <img src={`${API}/attachment/${p.file_id}?token=${sessionStorage.getItem(TOKEN_KEY)}`} alt={p.room || `foto ${i + 1}`} loading="lazy"
                    className="relative w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EDIT_ZONE = [
  ["centro", "Centro"], ["semicentro", "Semi-centro"], ["prima_periferia", "Prima periferia"],
  ["periferia", "Periferia"], ["extraurbano", "Extra-urbano"],
];
const EDIT_STATO = [
  ["al_grezzo", "Al grezzo / inagibile"],
  ["da_ristrutturare", "Da ristrutturare completamente"],
  ["parzialmente_da_ristrutturare", "Parzialmente da ristrutturare"],
  ["da_rinfrescare", "Da rinfrescare"],
  ["buono", "Buono / abitabile"],
  ["ristrutturato", "Ristrutturato / ottime condizioni"],
  ["nuovo_recente", "Nuovo / recente"],
  ["completamente_ristrutturato_mai_abitato", "Ristrutturato mai abitato"],
];
const EDIT_FLOORS = [["terra", "Piano terra"], ["1", "1° piano"], ["2", "2° piano"], ["3plus", "3° o superiore"], ["attico", "Attico"]];
const EDIT_TYPES = ["Appartamento", "Attico", "Monolocale", "Loft / Open space", "Casa a schiera", "Villa / Casa indipendente", "Garage / Posto auto", "Altro"];

function DailyChart() {
  const [data, setData] = useState(null);
  useEffect(() => {
    authApi().get("/admin/daily-stats?days=14").then((r) => setData(r.data)).catch(() => setData([]));
  }, []);
  if (!data) return null;
  const rows = data.map((d) => ({ ...d, giorno: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}` }));
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4" data-testid="daily-chart">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-semibold text-white">Video avviati vs Valutazioni completate</div>
          <div className="text-[10px] text-slate-400">Andamento giornaliero — ultimi 14 giorni</div>
        </div>
      </div>
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 5, right: 5, left: -22, bottom: 0 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis dataKey="giorno" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
            <ChartTooltip cursor={{ fill: "rgba(255,255,255,0.06)" }}
              contentStyle={{ background: "#0b2545", border: "1px solid rgba(20,184,166,0.4)", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#A9D6E5" }} itemStyle={{ color: "#fff" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="video_plays" name="Video avviati" fill="#14B8A6" radius={[3, 3, 0, 0]} />
            <Bar dataKey="leads" name="Valutazioni" fill="#A9D6E5" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const EDIT_FIELD_LABELS = {
  property_address: "Indirizzo", comune: "Comune", zona: "Zona", property_type: "Tipologia",
  property_sqm: "Mq", construction_year: "Anno", stato_conservativo: "Stato", floor: "Piano",
  elevator: "Ascensore", garage: "Garage", garden_sqm: "Giardino mq", terrazzo_sqm: "Terrazzo mq",
};

const fmtChangeVal = (v) => (v === true ? "Sì" : v === false ? "No" : v === null || v === undefined || v === "" ? "—" : String(v));

function EditHistory({ history }) {
  if (!history?.length) return null;
  return (
    <div className="mt-5 border-t border-white/10 pt-4" data-testid="lead-edit-history">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Storico modifiche ({history.length})</div>
      <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
        {[...history].reverse().map((h, i) => (
          <div key={i} className="rounded-lg bg-white/5 border border-white/10 p-2.5 text-xs" data-testid={`lead-edit-history-item-${i}`}>
            <div className="flex flex-wrap items-center justify-between gap-1">
              <span className="text-slate-400">{new Date(h.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })} · {h.by || "Admin"}</span>
              <span className="font-semibold text-teal-300">{fmtEur(h.value_before) || "—"} → {fmtEur(h.value_after) || "—"}</span>
            </div>
            {h.changes && Object.keys(h.changes).length > 0 && (
              <div className="mt-1 text-slate-300 leading-relaxed">
                {Object.entries(h.changes).map(([k, c]) => (
                  <span key={k} className="inline-block mr-3">
                    <b className="text-white">{EDIT_FIELD_LABELS[k] || k}</b>: {fmtChangeVal(c.from)} → <span className="text-teal-300">{fmtChangeVal(c.to)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadEdit({ lead, onClose, onSaved }) {
  const [f, setFields] = useState({
    property_address: lead.property_address || "",
    comune: lead.comune || "",
    zona: lead.zona || "",
    property_type: lead.property_type || "",
    property_sqm: lead.property_sqm ?? "",
    construction_year: lead.construction_year ?? "",
    stato_conservativo: lead.stato_conservativo || "",
    floor: lead.floor || "",
    elevator: lead.elevator === true ? "si" : lead.elevator === false ? "no" : "",
    garage: lead.garage === true ? "si" : lead.garage === false ? "no" : "",
    garden_sqm: lead.garden_sqm ?? "",
    terrazzo_sqm: lead.terrazzo_sqm ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [genPremium, setGenPremium] = useState(false);
  const [newVal, setNewVal] = useState(null);
  const setF = (p) => setFields((s) => ({ ...s, ...p }));
  const inCls = "w-full rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-400";
  const lbCls = "block text-[10px] uppercase tracking-wider text-slate-400 mb-1";

  const buildPayload = () => ({
    property_address: f.property_address.trim() || null,
    comune: f.comune.trim() || null,
    zona: f.zona || null,
    property_type: f.property_type || null,
    property_sqm: f.property_sqm !== "" ? Number(f.property_sqm) : null,
    construction_year: f.construction_year !== "" ? Number(f.construction_year) : null,
    stato_conservativo: f.stato_conservativo || null,
    floor: f.floor || null,
    elevator: f.elevator ? f.elevator === "si" : null,
    garage: f.garage ? f.garage === "si" : null,
    garden_sqm: f.garden_sqm !== "" ? Number(f.garden_sqm) : null,
    terrazzo_sqm: f.terrazzo_sqm !== "" ? Number(f.terrazzo_sqm) : null,
  });

  const save = async (resend) => {
    if (saving) return;
    if (f.property_sqm !== "" && (!Number.isFinite(Number(f.property_sqm)) || Number(f.property_sqm) <= 0)) { toast.error("Superficie non valida"); return; }
    setSaving(true);
    try {
      const { data } = await authApi().put(`/admin/leads/${lead.id}`, buildPayload());
      setNewVal(data.valuation);
      onSaved(data.lead);
      toast.success(`Valutazione ricalcolata: ${fmtEur(data.valuation?.estimated_value_median)}`);
      if (resend) {
        if (!lead.email) { toast.error("Il lead non ha un'email"); }
        else {
          const { data: r } = await authApi().post(`/admin/leads/${lead.id}/resend-email`);
          toast.success(`Email reinviata a ${r.sent_to}${r.with_pdf ? " (con PDF aggiornato)" : ""}`);
          onClose();
        }
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const generatePremium = async () => {
    if (genPremium) return;
    if (!window.confirm(`Generare la brochure Premium (11 pagine) e inviarla GRATUITAMENTE a ${lead.email}?`)) return;
    setGenPremium(true);
    try {
      const { data } = await authApi().post(`/admin/leads/${lead.id}/generate-premium`);
      toast.success(`💎 Brochure Premium ${data.protocol} inviata a ${data.sent_to}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Generazione Premium non riuscita");
    } finally {
      setGenPremium(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="lead-edit-modal">
      <div className="bg-[#0b2545] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Pencil className="w-4 h-4 text-teal-300" /> Modifica valutazione</h2>
            <p className="text-xs text-slate-400">{lead.full_name} · {lead.email || "senza email"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="lead-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center bg-white/5 border border-white/10 rounded-xl p-3 mb-4">
          <div><div className="text-[10px] text-slate-400 uppercase">Valore attuale</div><div className="text-sm font-semibold text-white">{fmtEur(lead.valuation?.estimated_value_median) || "—"}</div></div>
          <div><div className="text-[10px] text-teal-300 uppercase">Nuovo valore</div><div className="text-sm font-bold text-teal-300" data-testid="lead-edit-new-value">{newVal ? fmtEur(newVal.estimated_value_median) : "—"}</div></div>
          <div><div className="text-[10px] text-slate-400 uppercase">Range nuovo</div><div className="text-xs font-medium text-white">{newVal ? `${fmtEur(newVal.estimated_value_min)} – ${fmtEur(newVal.estimated_value_max)}` : "—"}</div></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={lbCls}>Indirizzo immobile</label>
            <input value={f.property_address} onChange={(e) => setF({ property_address: e.target.value })} className={inCls} data-testid="lead-edit-address" />
          </div>
          <div>
            <label className={lbCls}>Comune</label>
            <input value={f.comune} onChange={(e) => setF({ comune: e.target.value })} className={inCls} data-testid="lead-edit-comune" />
          </div>
          <div>
            <label className={lbCls}>Zona</label>
            <select value={f.zona} onChange={(e) => setF({ zona: e.target.value })} className={inCls} data-testid="lead-edit-zona">
              <option value="">—</option>
              {EDIT_ZONE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbCls}>Tipologia</label>
            <select value={f.property_type} onChange={(e) => setF({ property_type: e.target.value })} className={inCls} data-testid="lead-edit-tipologia">
              <option value="">—</option>
              {EDIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={lbCls}>Superficie commerciale (mq)</label>
            <input value={f.property_sqm} onChange={(e) => setF({ property_sqm: e.target.value })} inputMode="numeric" className={inCls} data-testid="lead-edit-mq" />
          </div>
          <div>
            <label className={lbCls}>Anno costruzione</label>
            <input value={f.construction_year} onChange={(e) => setF({ construction_year: e.target.value })} inputMode="numeric" className={inCls} data-testid="lead-edit-anno" />
          </div>
          <div>
            <label className={lbCls}>Stato conservativo</label>
            <select value={f.stato_conservativo} onChange={(e) => setF({ stato_conservativo: e.target.value })} className={inCls} data-testid="lead-edit-stato">
              <option value="">—</option>
              {EDIT_STATO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbCls}>Piano</label>
            <select value={f.floor} onChange={(e) => setF({ floor: e.target.value })} className={inCls} data-testid="lead-edit-piano">
              <option value="">—</option>
              {EDIT_FLOORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbCls}>Ascensore</label>
            <select value={f.elevator} onChange={(e) => setF({ elevator: e.target.value })} className={inCls} data-testid="lead-edit-ascensore">
              <option value="">—</option><option value="si">Sì</option><option value="no">No</option>
            </select>
          </div>
          <div>
            <label className={lbCls}>Garage / posto auto</label>
            <select value={f.garage} onChange={(e) => setF({ garage: e.target.value })} className={inCls} data-testid="lead-edit-garage">
              <option value="">—</option><option value="si">Sì</option><option value="no">No</option>
            </select>
          </div>
          <div>
            <label className={lbCls}>Giardino (mq)</label>
            <input value={f.garden_sqm} onChange={(e) => setF({ garden_sqm: e.target.value })} inputMode="numeric" className={inCls} data-testid="lead-edit-giardino" />
          </div>
          <div>
            <label className={lbCls}>Terrazzo (mq)</label>
            <input value={f.terrazzo_sqm} onChange={(e) => setF({ terrazzo_sqm: e.target.value })} inputMode="numeric" className={inCls} data-testid="lead-edit-terrazzo" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <button onClick={() => save(false)} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-white text-xs font-semibold hover:border-teal-400 disabled:opacity-60"
            data-testid="lead-edit-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Ricalcola e salva
          </button>
          <button onClick={() => save(true)} disabled={saving || !lead.email}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-500 text-[#081c33] text-xs font-bold hover:bg-teal-400 disabled:opacity-60"
            data-testid="lead-edit-save-resend">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Ricalcola, salva e reinvia al cliente
          </button>
          <button onClick={generatePremium} disabled={genPremium || saving || !lead.email}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-400 text-[#081c33] text-xs font-bold hover:bg-amber-300 disabled:opacity-60"
            data-testid="lead-generate-premium">
            {genPremium ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>💎</>}
            Genera Premium e invia (omaggio)
          </button>
        </div>
        <p className="text-[10px] text-slate-500 mt-2">Il reinvio manda al cliente l'email con la valutazione aggiornata e il PDF ricalcolato.</p>
        <EditHistory history={lead.edit_history} />
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN_KEY));
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [upgrades, setUpgrades] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("leads");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [omi, setOmi] = useState(null);
  const [omiRefreshing, setOmiRefreshing] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [betaOnly, setBetaOnly] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null);
  const [shares, setShares] = useState([]);
  const [qrUrl, setQrUrl] = useState(null);
  const [premium, setPremium] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [agencyLeads, setAgencyLeads] = useState([]);

  const logout = useCallback(() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authApi();
      const [s, l, c, u, o, f, sh, ps, rv, ag] = await Promise.all([api.get("/admin/stats"), api.get("/admin/leads"), api.get("/admin/call-requests"), api.get("/admin/upgrade-requests"), api.get("/admin/omi-status"), api.get("/admin/beta-feedback"), api.get("/admin/share-events"), api.get("/admin/premium-stats"), api.get("/admin/premium-reviews"), api.get("/admin/agency-leads")]);
      setStats(s.data); setLeads(l.data); setCalls(c.data); setUpgrades(u.data); setOmi(o.data); setFeedbacks(f.data); setShares(sh.data); setPremium(ps.data); setReviews(rv.data); setAgencyLeads(ag.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); toast.error("Sessione scaduta, accedi di nuovo"); }
      else toast.error("Errore nel caricamento dati");
    } finally { setLoading(false); }
  }, [logout]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  useEffect(() => {
    if (!authed) return undefined;
    let objUrl;
    authApi().get("/admin/qr-code", { responseType: "blob" })
      .then(({ data }) => { objUrl = URL.createObjectURL(data); setQrUrl(objUrl); })
      .catch(() => {});
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [authed]);

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl; a.download = "qr-eneaspecialist.png"; a.click();
    toast.success("QR code scaricato");
  };

  const downloadBrochure = async () => {
    try {
      const { data } = await authApi().get("/admin/brochure-agenzie", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "ENEA-Specialist-Brochure-Agenzie.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Brochure agenzie scaricata");
    } catch (e) {
      toast.error("Download brochure non riuscito");
    }
  };

  const toggleAgencyContacted = async (a) => {
    try {
      await authApi().put(`/admin/agency-leads/${a.id}`, { contacted: !a.contacted });
      setAgencyLeads((list) => list.map((x) => (x.id === a.id ? { ...x, contacted: !a.contacted } : x)));
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const refreshOmi = async () => {
    setOmiRefreshing(true);
    try {
      const { data } = await authApi().post("/admin/omi-refresh");
      if (data.error) toast.error(`Aggiornamento OMI non riuscito: ${data.error}`);
      else toast.success(data.updated ? `Banca dati OMI aggiornata: ${Number(data.quotes).toLocaleString("it-IT")} quotazioni (${data.semestre}° sem. ${data.anno})` : "Nessuna novità dalla fonte OMI");
      const { data: st } = await authApi().get("/admin/omi-status");
      setOmi(st);
    } catch { toast.error("Errore aggiornamento OMI"); }
    finally { setOmiRefreshing(false); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await authApi().get("/admin/leads-export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `enea-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("CSV esportato");
    } catch { toast.error("Errore export CSV"); }
  };

  if (!authed) return <LoginGate onLogin={() => setAuthed(true)} />;

  const q = search.trim().toLowerCase();
  const searchedLeads = q
    ? leads.filter((l) => [l.full_name, l.owner_name, l.email, l.phone, l.property_address, l.comune, l.address_geo?.city, l.id]
        .some((v) => (v || "").toLowerCase().includes(q)))
    : leads;
  const todayKey = new Date().toISOString().slice(0, 10);
  const quickFiltered = quickFilter === "today"
    ? searchedLeads.filter((l) => (l.timestamp || "").slice(0, 10) === todayKey)
    : quickFilter === "certificata"
      ? searchedLeads.filter((l) => l.valuation_mode === "certificata")
      : searchedLeads;
  const filteredLeads = betaOnly ? quickFiltered.filter((l) => l.beta) : quickFiltered;
  const betaCount = leads.filter((l) => l.beta).length;

  const dailyData = (() => {
    const days = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }), count: 0 });
    }
    const map = Object.fromEntries(days.map((d) => [d.key, d]));
    leads.forEach((l) => {
      const k = (l.timestamp || "").slice(0, 10);
      if (map[k]) map[k].count += 1;
    });
    return days;
  })();

  const deleteLead = async (ev, l) => {
    ev.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente la richiesta di "${l.full_name}"? L'operazione non si può annullare.`)) return;
    try {
      await authApi().delete(`/admin/leads/${l.id}`);
      setLeads((prev) => prev.filter((x) => x.id !== l.id));
      toast.success("Richiesta eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const refundTxn = async (t) => {
    if (!window.confirm(`Rimborsare €${t.amount_eur} a "${t.billing_name || t.name}" via PayPal?\nL'operazione è definitiva e libera un posto dell'offerta lancio.`)) return;
    try {
      await authApi().post(`/admin/premium-refund/${t.session_id}`);
      toast.success(`Rimborso di €${t.amount_eur} eseguito su PayPal ↩️`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Rimborso non riuscito");
    }
  };

  const moderateReview = async (r, approved) => {
    try {
      await authApi().post(`/admin/premium-reviews/${r.id}/moderate`, { approved });
      setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, approved } : x)));
      toast.success(approved ? "Recensione approvata — ora è visibile in home ⭐" : "Recensione nascosta dalla home");
    } catch { toast.error("Operazione non riuscita"); }
  };

  const deleteReview = async (r) => {
    if (!window.confirm(`Eliminare definitivamente la recensione di "${r.name}"?`)) return;
    try {
      await authApi().delete(`/admin/premium-reviews/${r.id}`);
      setReviews((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Recensione eliminata");
    } catch { toast.error("Eliminazione non riuscita"); }
  };

  return (
    <div className="min-h-screen bg-[#081c33] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0b2545]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.22em] text-teal-300 uppercase">Gruppo Vela · Area Riservata</div>
            <h1 className="text-lg font-semibold">Pannello Richieste</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" onClick={exportCsv} className="bg-teal-500 hover:bg-teal-400 text-[#081c33] font-semibold" data-testid="admin-export-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
          <StatCard icon={Users} label="Richieste totali" value={stats?.total_leads} testId="stat-total"
            onClick={() => { setTab("leads"); setQuickFilter(null); setBetaOnly(false); setSearch(""); }} />
          <StatCard icon={CalendarClock} label="Oggi" value={stats?.leads_today} testId="stat-today"
            onClick={() => { setTab("leads"); setQuickFilter("today"); setBetaOnly(false); }} />
          <StatCard icon={Camera} label="Certificate" value={stats?.certificata} testId="stat-certificata"
            onClick={() => { setTab("leads"); setQuickFilter("certificata"); setBetaOnly(false); }} />
          <StatCard icon={Euro} label="Incasso Premium" value={premium ? `€ ${Number(premium.revenue_eur).toLocaleString("it-IT")}` : "—"} testId="stat-premium"
            onClick={() => setTab("premium")} />
          <StatCard icon={Phone} label="Richieste call" value={stats?.call_requests} testId="stat-calls"
            onClick={() => setTab("calls")} />
          <StatCard icon={Share2} label={`Condivisioni WA${stats?.whatsapp_shares != null ? ` (Video ${stats.whatsapp_shares_video ?? 0} · Report ${(stats.whatsapp_shares ?? 0) - (stats.whatsapp_shares_video ?? 0)})` : ""}`} value={stats?.whatsapp_shares} testId="stat-shares"
            onClick={() => setTab("shares")} />
          <StatCard icon={PlayCircle} label={`Video avviati${stats?.video_plays != null ? ` (H ${stats.video_plays_home} · V ${stats.video_plays_valutazione})` : ""}`} value={stats?.video_plays} testId="stat-video-plays" />
        </div>

        <DailyChart />

        <div className="bg-[#0b2545] border border-white/10 rounded-xl p-4" data-testid="admin-daily-chart">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-3">📊 Richieste per giorno · ultimi 14 giorni</div>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} interval={1} />
                <YAxis stroke="#64748b" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ChartTooltip cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={{ background: "#081c33", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#a9d6e5" }} itemStyle={{ color: "#14b8a6" }} formatter={(v) => [v, "richieste"]} />
                <Bar dataKey="count" fill="#14b8a6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#0b2545] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-omi-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/15 flex items-center justify-center shrink-0">
              <Database className="w-5 h-5 text-teal-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Banca dati OMI · Agenzia delle Entrate</div>
              <div className="text-[11px] text-slate-400 mt-0.5" data-testid="admin-omi-info">
                {omi ? <>
                  {omi.semestre ? `${omi.semestre}° sem. ${omi.anno}` : "—"} · {Number(omi.quotes_count ?? 0).toLocaleString("it-IT")} quotazioni
                  {omi.last_check ? ` · ultimo controllo: ${fmtDate(omi.last_check)}` : ""}
                  {omi.status && omi.status !== "ok" ? " · ⚠️ ultimo controllo fallito" : ""}
                </> : "—"}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Controllo automatico ogni 24h — il database si aggiorna da solo quando esce il nuovo semestre</div>
            </div>
          </div>
          <Button size="sm" onClick={refreshOmi} disabled={omiRefreshing} className="bg-white/10 hover:bg-white/20 text-white" data-testid="admin-omi-refresh-btn">
            {omiRefreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Aggiorna ora
          </Button>
        </div>

        <ComparablesCard />

        <div className="bg-[#0b2545] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-qr-card">
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-lg bg-white p-1.5 flex items-center justify-center shrink-0">
              {qrUrl ? <img src={qrUrl} alt="QR code valutazione" className="w-full h-full object-contain" data-testid="admin-qr-img" /> : <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
            </div>
            <div>
              <div className="text-sm font-semibold text-white flex items-center gap-2"><QrCode className="w-4 h-4 text-teal-300" /> QR Code · volantini e vetrine</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Punta a <span className="text-teal-300">https://enea-valutazione.emergent.host</span> — la stessa identica destinazione dei QR già in circolazione: restano tutti validi.</div>
              <div className="text-[10px] text-slate-500 mt-0.5">PNG ad alta risoluzione (~1000px), pronto per la stampa.</div>
            </div>
          </div>
          <Button size="sm" onClick={downloadQr} disabled={!qrUrl} className="bg-teal-500 hover:bg-teal-400 text-[#081c33] font-semibold" data-testid="admin-qr-download">
            <Download className="w-4 h-4 mr-1.5" /> Scarica PNG
          </Button>
        </div>

        <div className="bg-[#0b2545] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="admin-brochure-card">
          <div>
            <div className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="w-4 h-4 text-teal-300" /> Brochure Agenzie · &quot;Spietata. Precisa. Affidabile.&quot;</div>
            <div className="text-[11px] text-slate-400 mt-0.5">PDF di 4 pagine per le agenzie immobiliari: i buchi neri degli altri sistemi di valutazione, il confronto diretto e il protocollo Zero Vincoli. Pronto da stampare o inviare.</div>
          </div>
          <Button size="sm" onClick={downloadBrochure} className="bg-teal-500 hover:bg-teal-400 text-[#081c33] font-semibold" data-testid="admin-brochure-download">
            <Download className="w-4 h-4 mr-1.5" /> Scarica PDF
          </Button>
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <button onClick={() => setTab("leads")} data-testid="admin-tab-leads"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "leads" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Valutazioni ({leads.length})
          </button>
          <button onClick={() => setTab("premium")} data-testid="admin-tab-premium"
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${tab === "premium" ? "bg-amber-400 text-[#081c33]" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"}`}>
            💎 Premium ({premium?.sold_count ?? 0})
          </button>
          <button onClick={() => setTab("upgrades")} data-testid="admin-tab-upgrades"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "upgrades" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Upgrade ({upgrades.length})
          </button>
          <button onClick={() => setTab("calls")} data-testid="admin-tab-calls"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "calls" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Call ({calls.length})
          </button>
          <button onClick={() => setTab("feedback")} data-testid="admin-tab-feedback"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "feedback" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Feedback Beta ({feedbacks.length})
          </button>
          <button onClick={() => setTab("agenzie")} data-testid="admin-tab-agenzie"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "agenzie" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            🏢 Agenzie ({agencyLeads.length})
          </button>
          <button onClick={() => setTab("radar")} data-testid="admin-tab-radar"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "radar" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            🎯 Concorrenti
          </button>
          <button onClick={() => setTab("shares")} data-testid="admin-tab-shares"
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "shares" ? "bg-teal-500 text-[#081c33]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Condivisioni ({shares.length})
          </button>
          {tab === "leads" && quickFilter && (
            <button onClick={() => setQuickFilter(null)} data-testid="admin-quick-filter-chip"
              className="px-4 py-1.5 rounded-full text-sm font-medium bg-teal-500 text-[#081c33] inline-flex items-center gap-1.5">
              {quickFilter === "today" ? "📅 Solo oggi" : "⭐ Solo Certificate"} <X className="w-3.5 h-3.5" />
            </button>
          )}
          {tab === "leads" && (
            <button onClick={() => setBetaOnly((b) => !b)} data-testid="admin-beta-filter"
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${betaOnly ? "bg-amber-500 text-[#081c33]" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"}`}>
              🧪 Solo BETA ({betaCount})
            </button>
          )}
          {tab === "leads" && (
            <div className="relative flex-1 min-w-[220px] max-w-sm ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nome, comune, telefono…"
                className="w-full pl-9 pr-8 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                data-testid="admin-search-input" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid="admin-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {tab === "leads" && (
          <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-4 py-3">Data</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Contatti</th>
                  <th className="px-4 py-3">Immobile</th><th className="px-4 py-3">Modalità</th><th className="px-4 py-3">Valutazione</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">{q ? `Nessun risultato per "${search}"` : "Nessuna richiesta ancora registrata"}</td></tr>
                )}
                {filteredLeads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(l.timestamp)}</td>
                    <td className="px-4 py-3 font-medium">
                      {l.full_name}
                      {l.beta && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full font-bold align-middle" data-testid={`lead-beta-tag-${l.id}`}>BETA</span>}
                      {(l.sale_intent === "vendo_subito" || l.sale_intent === "entro_3_mesi") && <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded-full font-bold align-middle" data-testid={`lead-hot-tag-${l.id}`}>🔥 HOT</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      <div className="text-xs">{l.email}</div>
                      {l.phone && <div className="text-xs text-slate-400">{l.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      <div className="text-xs max-w-[220px] truncate">{l.property_address || "—"}</div>
                      <div className="text-xs text-slate-400">{[l.property_type, l.property_sqm ? `${l.property_sqm} mq` : null].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${l.valuation_mode === "certificata" ? "bg-teal-500/15 text-teal-300" : "bg-white/10 text-slate-300"}`}>
                        {l.valuation_mode === "certificata" ? `Certificata${l.photo_tier ? ` · ${l.photo_tier}` : ""}` : "Classica"}
                      </span>
                      <span className="ml-1.5"><GpsBadge value={l.gps_match} /></span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-teal-300 whitespace-nowrap">{fmtEur(l.valuation?.estimated_value_median)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <button onClick={(ev) => { ev.stopPropagation(); setSelected(l); }} className="text-slate-400 hover:text-teal-300 transition-colors" title="Vedi dettaglio" data-testid={`lead-view-${l.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={(ev) => { ev.stopPropagation(); setEditing(l); }} className="text-slate-500 hover:text-teal-300 transition-colors" title="Modifica / Reinvia" data-testid={`lead-edit-${l.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={(ev) => deleteLead(ev, l)} className="text-slate-500 hover:text-red-400 transition-colors" title="Elimina richiesta" data-testid={`lead-delete-${l.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "premium" && (
          <div className="space-y-4" data-testid="admin-premium-section">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Contatore acquisti Premium", value: premium ? `${premium.sold_count} / prime ${premium.launch_quota} a €${premium.launch_price_eur}` : "—", testid: "premium-sold" },
                { label: "Incasso totale (PayPal)", value: premium ? `€ ${Number(premium.revenue_eur).toLocaleString("it-IT")}` : "—", testid: "premium-revenue" },
                { label: "In attesa di pagamento", value: premium?.pending_count ?? "—", testid: "premium-pending" },
                { label: `Offerta lancio €${premium?.launch_price_eur ?? 249}`, value: premium ? (premium.launch_remaining > 0 ? `restano ${premium.launch_remaining}/${premium.launch_quota}` : `finita — ora €${premium.standard_price_eur ?? 279}`) : "—", testid: "premium-launch" },
              ].map((k) => (
                <div key={k.label} className="bg-[#0b2545] border border-white/10 rounded-xl px-4 py-3" data-testid={k.testid}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{k.label}</div>
                  <div className="text-xl font-bold text-amber-300 mt-0.5">{k.value}</div>
                </div>
              ))}
            </div>

            <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-premium-table">
              <div className="px-4 pt-4 pb-1 text-sm font-semibold">💳 Pagamenti Premium ricevuti (PayPal)</div>
              <table className="w-full text-sm min-w-[980px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                    <th className="px-4 py-3">#</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Dati fiscali</th><th className="px-4 py-3">Immobile</th>
                    <th className="px-4 py-3">Importo</th><th className="px-4 py-3">Offerta</th>
                  </tr>
                </thead>
                <tbody>
                  {(premium?.transactions || []).length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nessuna Premium venduta ancora — l&apos;offerta lancio è attiva 🔥</td></tr>
                  )}
                  {(premium?.transactions || []).map((t) => (
                    <tr key={t.session_id} className="border-b border-white/5" data-testid={`premium-txn-${t.session_id}`}>
                      <td className="px-4 py-3 font-bold text-amber-300">{t.n}</td>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(t.date)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{t.name || "—"}</div>
                        <div className="text-xs text-slate-400">{t.email || ""}</div>
                      </td>
                      <td className="px-4 py-3" data-testid={`premium-fiscal-${t.session_id}`}>
                        <div className="text-xs font-medium">{t.billing_name || "—"}</div>
                        <div className="text-[11px] text-teal-300 font-mono">{t.tax_kind === "piva" ? "P.IVA" : "CF"} {t.tax_code || "—"}</div>
                        <div className="text-[10px] text-slate-400 max-w-[220px] truncate">{t.billing_address || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-300"><div className="text-xs max-w-[200px] truncate">{t.address || t.comune || "—"}</div></td>
                      <td className="px-4 py-3 font-bold text-teal-300 whitespace-nowrap">€ {Number(t.amount_eur).toLocaleString("it-IT")}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {t.payment_status === "refunded" ? (
                            <span className="text-[10px] font-bold text-red-300 bg-red-500/15 rounded-full px-2 py-0.5 w-fit" data-testid={`premium-refunded-${t.session_id}`}>↩️ RIMBORSATA</span>
                          ) : t.launch_offer
                            ? <span className="text-[10px] font-bold text-amber-300 bg-amber-500/15 rounded-full px-2 py-0.5 w-fit">🔥 LANCIO €249</span>
                            : <span className="text-[10px] text-slate-400 bg-white/5 rounded-full px-2 py-0.5 w-fit">Standard €279</span>}
                          {t.visura_requested && (
                            <span className="text-[10px] font-bold text-sky-300 bg-sky-500/15 rounded-full px-2 py-0.5 w-fit" data-testid={`premium-visura-${t.session_id}`}>📄 VISURA +€5</span>
                          )}
                          {t.payment_status === "paid" && (
                            <button onClick={() => refundTxn(t)}
                              className="text-[10px] font-semibold text-red-300 border border-red-400/40 rounded-full px-2 py-0.5 w-fit hover:bg-red-500/15 transition"
                              data-testid={`premium-refund-${t.session_id}`}>
                              ↩️ Rimborsa
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-reviews-table">
              <div className="px-4 pt-4 pb-1 text-sm font-semibold">⭐ Recensioni clienti Premium — approva per mostrarle in home</div>
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                    <th className="px-4 py-3">Data</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Origine</th><th className="px-4 py-3">Voto</th>
                    <th className="px-4 py-3">Commento</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nessuna recensione ancora ricevuta — arrivano dall&apos;email post-brochure (3 giorni dopo) e dal form in homepage</td></tr>
                  )}
                  {reviews.map((r) => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`review-row-${r.id}`}>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(r.timestamp)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.name}</div>
                        {r.comune && <div className="text-xs text-slate-400">{r.comune}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {(r.verified ?? r.source !== "homepage")
                          ? <span className="text-[10px] font-bold text-teal-300 bg-teal-500/15 rounded-full px-2 py-0.5">💎 PREMIUM</span>
                          : <span className="text-[10px] text-slate-300 bg-white/5 rounded-full px-2 py-0.5">Sito web</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap"><FeedbackStars n={r.rating} /></td>
                      <td className="px-4 py-3 text-xs text-slate-300 max-w-[280px]">{r.comment || <span className="text-slate-500">—</span>}</td>
                      <td className="px-4 py-3">
                        {r.approved
                          ? <span className="text-[10px] font-bold text-teal-300 bg-teal-500/15 rounded-full px-2 py-0.5">IN HOME ✓</span>
                          : <span className="text-[10px] font-bold text-slate-400 bg-white/5 rounded-full px-2 py-0.5">DA APPROVARE</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.approved ? (
                            <button onClick={() => moderateReview(r, false)} title="Nascondi dalla home"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 text-slate-300 text-xs font-semibold hover:bg-white/20"
                              data-testid={`review-hide-${r.id}`}>
                              <EyeOff className="w-3.5 h-3.5" /> Nascondi
                            </button>
                          ) : (
                            <button onClick={() => moderateReview(r, true)} title="Approva e mostra in home"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-teal-500 text-[#081c33] text-xs font-bold hover:bg-teal-400"
                              data-testid={`review-approve-${r.id}`}>
                              <Check className="w-3.5 h-3.5" /> Approva
                            </button>
                          )}
                          <button onClick={() => deleteReview(r)} className="text-slate-500 hover:text-red-400 transition-colors" title="Elimina recensione" data-testid={`review-delete-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "upgrades" && (
          <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-upgrades-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-4 py-3">Data richiesta</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Contatti</th>
                  <th className="px-4 py-3">Livello richiesto</th><th className="px-4 py-3">Livello attuale</th><th className="px-4 py-3">Pratica</th>
                </tr>
              </thead>
              <tbody>
                {upgrades.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nessuna richiesta di upgrade</td></tr>
                )}
                {upgrades.map((u) => (
                  <tr key={u.id} className="border-b border-white/5">
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(u.timestamp)}</td>
                    <td className="px-4 py-3 font-medium">{u.full_name}</td>
                    <td className="px-4 py-3 text-slate-300">
                      <div className="text-xs">{u.email}</div>
                      {u.phone && <div className="text-xs text-slate-400">{u.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold ${u.requested_tier === "premium" ? "bg-amber-500/15 text-amber-300" : "bg-teal-500/15 text-teal-300"}`}>
                        <ArrowUpCircle className="w-3 h-3" /> {u.requested_tier === "premium" ? "Premium" : "Intermedia"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300 capitalize">{u.current_tier || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{u.protocol_code || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "agenzie" && (
          <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-agenzie-section">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-4 py-3">Data</th><th className="px-4 py-3">Agenzia</th><th className="px-4 py-3">Referente</th>
                  <th className="px-4 py-3">Contatti</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Messaggio</th><th className="px-4 py-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {agencyLeads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nessuna richiesta dalle agenzie ancora. Condividi eneaspecialist.it/agenzie</td></tr>
                )}
                {agencyLeads.map((a) => (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`agency-row-${a.id}`}>
                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(a.timestamp)}</td>
                    <td className="px-4 py-3 font-semibold text-white">{a.agency_name}{a.city ? <div className="text-[10px] text-slate-400 font-normal">{a.city}</div> : null}</td>
                    <td className="px-4 py-3 text-slate-300">{a.contact_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-300">{a.email}{a.phone ? <div>{a.phone}</div> : null}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold ${a.source === "feedback" ? "bg-sky-500/20 text-sky-300" : "bg-teal-500/20 text-teal-300"}`}>
                        {a.source === "feedback" ? "Feedback" : "Partnership"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400 max-w-[240px]"><div className="line-clamp-3">{a.message || "—"}</div></td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleAgencyContacted(a)} data-testid={`agency-contacted-${a.id}`}
                        className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${a.contacted ? "bg-teal-500/20 text-teal-300" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"}`}>
                        {a.contacted ? "✓ Contattata" : "Da contattare"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "radar" && <CompetitorRadar />}

        {tab === "shares" && (
          <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-shares-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-4 py-3">Data</th><th className="px-4 py-3">Chi ha condiviso</th>
                  <th className="px-4 py-3">Canale</th><th className="px-4 py-3">Pratica</th><th className="px-4 py-3">Beta</th>
                </tr>
              </thead>
              <tbody>
                {shares.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nessuna condivisione ancora registrata</td></tr>
                )}
                {shares.map((s) => (
                  <tr key={s.id} className="border-b border-white/5" data-testid={`share-row-${s.id}`}>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(s.timestamp)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{s.lead_name || "Anonimo"}</div>
                      <div className="text-xs text-slate-400">{[s.lead_email, s.lead_comune].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 bg-emerald-500/10 rounded-full px-2.5 py-1">WhatsApp</span></td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {s.protocol_code?.startsWith("VIDEO-")
                        ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-300 bg-sky-500/10 rounded-full px-2.5 py-1">🎬 Video · {s.protocol_code.replace("VIDEO-", "").toLowerCase()}</span>
                        : (s.protocol_code || "—")}
                    </td>
                    <td className="px-4 py-3">{s.beta ? <span className="text-[10px] font-bold text-amber-300 bg-amber-500/15 rounded-full px-2 py-0.5">BETA</span> : <span className="text-slate-600">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "feedback" && (
          <div className="space-y-4" data-testid="admin-feedback-section">
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Facilità d'uso (media)", value: feedbacks.length ? (feedbacks.reduce((a, f) => a + (f.ease_rating || 0), 0) / feedbacks.length).toFixed(1) + " / 5" : "—", testid: "feedback-avg-ease" },
                { label: "Fiducia nella stima (media)", value: feedbacks.length ? (feedbacks.reduce((a, f) => a + (f.trust_rating || 0), 0) / feedbacks.length).toFixed(1) + " / 5" : "—", testid: "feedback-avg-trust" },
                { label: "Feedback totali", value: feedbacks.length, testid: "feedback-total" },
              ].map((k) => (
                <div key={k.label} className="bg-[#0b2545] border border-white/10 rounded-xl px-4 py-3" data-testid={k.testid}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{k.label}</div>
                  <div className="text-xl font-bold text-amber-300 mt-0.5">{k.value}</div>
                </div>
              ))}
            </div>
            <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-feedback-table">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                    <th className="px-4 py-3">Data</th><th className="px-4 py-3">Beta tester</th><th className="px-4 py-3">Facilità</th>
                    <th className="px-4 py-3">Fiducia</th><th className="px-4 py-3">Note</th><th className="px-4 py-3">Pratica</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nessun feedback beta ancora ricevuto</td></tr>
                  )}
                  {feedbacks.map((f) => (
                    <tr key={f.id} className="border-b border-white/5" data-testid={`feedback-row-${f.id}`}>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(f.timestamp)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{f.lead_name || "—"}</div>
                        <div className="text-xs text-slate-400">{[f.lead_email, f.lead_comune].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap"><FeedbackStars n={f.ease_rating} /></td>
                      <td className="px-4 py-3 whitespace-nowrap"><FeedbackStars n={f.trust_rating} /></td>
                      <td className="px-4 py-3 text-xs text-slate-300 max-w-[280px]">{f.comments || <span className="text-slate-500">—</span>}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{f.protocol_code || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "calls" && (
          <div className="bg-[#0b2545] border border-white/10 rounded-xl overflow-x-auto" data-testid="admin-calls-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-4 py-3">Data richiesta</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Contatti</th>
                  <th className="px-4 py-3">Giorno preferito</th><th className="px-4 py-3">Fascia</th><th className="px-4 py-3">Pratica</th>
                </tr>
              </thead>
              <tbody>
                {calls.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nessuna richiesta di call</td></tr>
                )}
                {calls.map((c) => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(c.timestamp)}</td>
                    <td className="px-4 py-3 font-medium">{c.full_name}</td>
                    <td className="px-4 py-3 text-slate-300">
                      <div className="text-xs">{c.email}</div>
                      {c.phone && <div className="text-xs text-slate-400">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{c.preferred_date}</td>
                    <td className="px-4 py-3 text-slate-300">{c.preferred_slot}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{c.protocol_code || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} />}
      {editing && <LeadEdit lead={editing} onClose={() => setEditing(null)}
        onSaved={(u) => { setLeads((prev) => prev.map((x) => (x.id === u.id ? u : x))); setEditing(u); }} />}
    </div>
  );
}
